'use client';
import {
  customerInformationSchema,
  removeLeadingZero,
} from '@payments-checkout/shared/helpers/constants/validation-input';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  countryCode,
  findCountryCode,
} from '@payments-checkout/shared/helpers/constants/country-code.const';
import {
  DOCUMENT_TYPE,
  documentTypesKeys,
  isCnpj,
  isCpf,
  isPassport
} from '@payments-checkout/shared/helpers/constants/document-person-type.const';
import { useHandleRedirectByError } from '@payments-checkout/shared/helpers/hooks/useHandleRedirectByError';
import {
  allowOnlyLettersOnBeforeInput,
  StringManager,
} from '@payments-checkout/shared/helpers/index';
import { reportError } from '@payments-checkout/shared/observability';
import { analiticaInstance } from '@payments-checkout/shared/plugins/analitica/analitica.events';
import { resolvePaymentType } from '@payments-checkout/shared/plugins/analitica/analitica.helpers';
import { usePaymentMethod } from '@payments-checkout/shared/providers';
import { Alert, Divider, InputField, Select, Text } from '@stone-payments/jade';
import { useRouter } from 'next/navigation';
import {
  FormProvider,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useIntl } from 'react-intl';
import { DocumentInput } from '../../../components/documentInput/documentInput';
import { usePixFlow } from '../../../flows/pixFlow';
import { useCustomerData } from '../../../hooks/useCustomerData';
import { usePageSharedData } from '../../../hooks/usePageSharedData';
import { customerDataPayloadBuilder } from '../../helpers/customerForm.helper';
import { useFormTypedValues } from '../../providers/formValuesProvider.provider';
import { CustomerFields } from '../../types/customerInformation.types';
import { PhoneInput } from './phoneInput.component';
import { CustomerDataProps } from './types';

const FIELD_REFERENCES: Record<string, string> = {
  email: 'campo_email',
  name: 'campo_nome',
  cpf: 'campo_documento',
  cnpj: 'campo_documento',
  passport: 'campo_documento',
  phone: 'campo_telefone',
};

export const InputGrid = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="grid grid-cols-1 gap-jade-200 w-full sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2">
      {children}
    </div>
  );
};

export function CustomerForm(props: CustomerDataProps) {
  const intl = useIntl();
  const { push } = useRouter();
  const { formValues, setFormValues } = useFormTypedValues();
  const {
    paymentMethod,
    paymentWithClickToPay,
    isCustomerFormLoading,
    setIsCustomerFormLoading,
    setInstallments,
    setInstallmentsExplanationText,
    setSurchargesText,
  } = usePaymentMethod();
  const { redirectByCode } = useHandleRedirectByError();
  const isPix = paymentMethod === 'pix';
  const { paymentLinkIdData, isDeliveryEnabled, showAddressPage } =
    usePageSharedData();
  const pixFlow = usePixFlow();

  const { mutate: setCustomerData } = useCustomerData({
    onSuccess: () => {
      if (isPix) {
        pixFlow.goToFirstStep();
      } else {
        push(
          `/${props.paymentLinkId}/${isDeliveryEnabled || showAddressPage
            ? 'delivery-address'
            : 'card-data'
          }`
        );
      }
    },
    onError: (error) => {
      reportError(error, { source: 'customer_data' });
      redirectByCode(error.response?.status);
      setIsCustomerFormLoading(false);
    },
  });

  const rawCountryCode =
    formValues?.countryCode ||
    props.customer?.phones?.mobile_phone?.country_code;
  const defaultCountryCode =
    rawCountryCode?.toString().replace(/[^0-9]/g, '') ||
    `${countryCode[0].value}`;

  const methods = useForm<CustomerFields>({
    defaultValues: {
      email: formValues?.email || props.customer?.email,
      name: formValues?.name || props.customer?.name,
      documentType:
        formValues?.documentType ||
        props.customer?.document_type ||
        DOCUMENT_TYPE.CPF.toLowerCase(),
      countryCode: defaultCountryCode,
      phone:
        (formValues?.phone ?? '') ||
        removeLeadingZero(
          props.customer?.phones?.mobile_phone?.area_code ?? ''
        ) + (props.customer?.phones?.mobile_phone?.number ?? '') ||
        '',
      ...(isCpf(formValues?.documentType || props.customer?.document_type) && {
        cpf:
          formValues?.cpf ||
          StringManager.formatCpf(props.customer?.document),
      }),
      ...(isCnpj(formValues?.documentType || props.customer?.document_type) && {
        cnpj:
          formValues?.cnpj ||
          StringManager.formatCnpj(props.customer?.document),
      }),
      ...(isPassport(
        formValues?.documentType || props.customer?.document_type
      ) && {
        passport: formValues?.passport || props.customer?.document,
      }),
    },
    mode: 'onTouched',
    resolver: yupResolver<CustomerFields>(customerInformationSchema()),
  });

  const errors = methods.formState.errors;

  const sendAmplitudeEvents = () => {
    const paymentType = resolvePaymentType(paymentMethod);

    analiticaInstance.events.homePage.submitData(
      paymentWithClickToPay ? 'clicktopay' : paymentType
    );
  };

  const trackFieldFilled =
    (reference: string) => (event: { target: { value: string } }) => {
      if (event.target.value) {
        analiticaInstance.events.homePage.formFieldFilled(reference);
      }
    };

  const trackDocumentTypeSelected = (event: {
    target: { value: string };
  }) => {
    const selectedOption =
      event.target.value === 'passport' ? 'passaporte' : event.target.value;

    analiticaInstance.events.homePage.formDropdownSelected(
      'select_documento',
      selectedOption
    );
  };

  const trackCountryCodeSelected = (event: { target: { value: string } }) => {
    analiticaInstance.events.homePage.formDropdownSelected(
      'select_cod_pais',
      findCountryCode(event.target.value)
    );
  };

  const onInvalid: SubmitErrorHandler<CustomerFields> = (formErrors) => {
    Object.entries(formErrors).forEach(([field, error]) => {
      const reference = FIELD_REFERENCES[field];
      if (!reference) return;

      analiticaInstance.events.homePage.formFieldError(
        reference,
        error?.message ?? ''
      );
    });
  };

  const onSubmit: SubmitHandler<CustomerFields> = (data) => {
    setIsCustomerFormLoading(true);
    sendAmplitudeEvents();
    setInstallments(null);
    setInstallmentsExplanationText(null);
    setSurchargesText(null);

    const customerData = customerDataPayloadBuilder(data);
    setCustomerData({
      paymentLinkId: props.paymentLinkId,
      sessionId: props.sessionId,
      customerData: customerData,
    });
    setFormValues((prev) => ({ ...prev, ...data }));
  };

  const selectedDocumentType = useWatch({
    control: methods.control,
    name: 'documentType',
  });

  const selectedCountryCode = useWatch({
    control: methods.control,
    name: 'countryCode',
  });

  return (
    <FormProvider {...methods}>
      <form
        id="customer-form"
        onSubmit={methods.handleSubmit(onSubmit, onInvalid)}
        data-testid="checkout-customer-form"
        className="w-full"
      >
        <div className="flex flex-col p-jade-250 rounded-xl flex-wrap gap-jade-200 bg-jade-background-surface min-500:p-0 min-500:rounded-none">
          <Text variant="heading-small" color="high">
            {intl.formatMessage({ id: 'customerForm.title' })}
          </Text>
          <InputField
            label={intl.formatMessage({ id: 'customerForm.email' }) + ' *'}
            placeholder={intl.formatMessage({
              id: 'customerForm.emailExample',
            })}
            size="medium"
            type="email"
            supportText={
              errors?.email?.message &&
              intl.formatMessage({
                id: `customerForm.validate.${errors?.email?.message}`,
              })
            }
            error={!!errors.email?.message}
            disabled={isCustomerFormLoading}
            {...methods.register('email', {
              onBlur: trackFieldFilled('campo_email'),
            })}
          />
          <InputField
            label={intl.formatMessage({ id: 'customerForm.name' }) + ' *'}
            placeholder="Ana Cristina da Silva"
            key="name"
            size="medium"
            maxLength={64}
            type="text"
            supportText={
              errors?.name?.message &&
              intl.formatMessage({
                id: `customerForm.validate.${errors?.name?.message}`,
              })
            }
            error={!!errors.name?.message}
            disabled={isCustomerFormLoading}
            onBeforeInput={allowOnlyLettersOnBeforeInput}
            {...methods.register('name', {
              onBlur: trackFieldFilled('campo_nome'),
            })}
          />
          <InputGrid>
            <Select
              label={intl.formatMessage({ id: 'customerForm.document' }) + ' *'}
              required
              size="medium"
              options={documentTypesKeys.map((document, index) => ({
                key: `${document}-${index}`,
                value: documentTypesKeys[index],
                label: intl.formatMessage({
                  id: `customerForm.${document}`,
                }),
              }))}
              disabled={isCustomerFormLoading}
              {...methods.register('documentType', {
                onChange: trackDocumentTypeSelected,
              })}
            />
            <DocumentInput
              key={selectedDocumentType}
              documentType={selectedDocumentType}
              disabled={isCustomerFormLoading}
            />
          </InputGrid>
          <InputGrid>
            <Select
              label={intl.formatMessage({ id: 'customerForm.countryCode' })}
              options={countryCode.map((country, index) => {
                return {
                  key: `${country.value}-${country.label}-${index}`,
                  value: `${country.value}`,
                  label: country.label,
                };
              })}
              size="medium"
              disabled={isCustomerFormLoading}
              {...methods.register('countryCode', {
                onChange: trackCountryCodeSelected,
              })}
            />
            <PhoneInput
              selectedCountryCode={selectedCountryCode}
              disabled={isCustomerFormLoading}
            />
          </InputGrid>
          <div className="block md:hidden">
            <Divider direction="horizontal" size="small" />
          </div>
        </div>
        {selectedDocumentType === 'passport' && (
          <div className="mt-jade-300">
            <Alert
              variant="warning"
              showIcon
              description={intl.formatMessage({ id: 'customerForm.alert' })}
            />
          </div>
        )}
      </form>
    </FormProvider>
  );
}
